import { ChevronLeft, ChevronRight } from 'lucide-react'

/** The API's default page size (D-28). */
export const PAGE_SIZE = 100

export interface PagerProps {
  /** 1-based, as the API's `page`. */
  page: number
  /** The API's `pageSize`. */
  pageSize?: number
  /** The API's `total`. */
  total: number
  /** What is being counted, for example "errors · newest first". */
  noun: string
  onPage: (page: number) => void
}

/** Numbered pages, Prev/Next and "Showing a–b of N" (mockup `Pager.tsx`), driven by `{ page, pageSize, total }`. */
export function Pager({ page, pageSize = PAGE_SIZE, total, noun, onPage }: PagerProps) {
  if (!total) return null
  const pages = Math.max(1, Math.ceil(total / pageSize))
  const from = (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)
  return (
    <nav className="pager" aria-label="Pagination">
      <span className="muted">
        Showing{' '}
        <b>
          {from}–{to}
        </b>{' '}
        of <b>{total}</b> {noun}
      </span>
      <div className="row pager-pages">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={page <= 1}
          onClick={() => {
            onPage(page - 1)
          }}
        >
          <ChevronLeft aria-hidden /> Prev
        </button>
        {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            className={`btn btn-sm pager-page ${n === page ? 'btn-primary' : 'btn-ghost'}`}
            aria-label={`Page ${String(n)}`}
            aria-current={n === page ? 'page' : undefined}
            onClick={() => {
              onPage(n)
            }}
          >
            {n}
          </button>
        ))}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={page >= pages}
          onClick={() => {
            onPage(page + 1)
          }}
        >
          Next <ChevronRight aria-hidden />
        </button>
      </div>
    </nav>
  )
}
